import { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Sheet, Stack, Typography } from '@mui/joy';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import { API_URL } from '../api';
import { companyKind, validInn, companyPatch, companyConflicts, mergeCompany, lookupCompany } from '../shared/companyLookup';

// Lookup only suggests a patch. Saving remains the ordinary action of the form.
export default function CompanyLookup({ form, setForm, token, audience = 'staff', ownerKey = '', onDirty, disabled = false }) {
  const inn = String(form?.inn || '').replace(/\s/g, '');
  const kind = companyKind(form?.client_type);
  const scope = `${audience}:${ownerKey}:${token || ''}:${inn}:${kind}`;
  const current = useRef({ scope, form });
  current.current = { scope, form };
  const [result, setResult] = useState(null);
  const [pending, setPending] = useState(null);
  const [retry, setRetry] = useState(0);
  const [notice, setNotice] = useState(null);
  const eligible = !!token && !!kind && validInn(inn) && (kind === 'LEGAL' ? inn.length === 10 : inn.length === 12);

  useEffect(() => {
    if (!eligible || disabled) return undefined;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      if (controller.signal.aborted) return;
      setResult({ scope, loading: true });
      try {
        const response = await lookupCompany(API_URL, { inn, kind, token, audience, signal: controller.signal });
        if (!controller.signal.aborted && current.current.scope === scope) setResult({ scope, response });
      } catch (error) {
        if (!controller.signal.aborted && current.current.scope === scope) setResult({ scope, error: error.message });
      }
    }, 500);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [scope, retry, eligible, disabled]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!kind) return null;
  const visible = result?.scope === scope ? result : null;
  const apply = (suggestion, replace = false) => {
    if (current.current.scope !== scope || suggestion.inn !== inn) return;
    const patch = companyPatch(suggestion);
    setForm(previous => mergeCompany(previous, patch, replace));
    setPending(null); setNotice({ scope, value: 'Реквизиты заполнены. Проверьте их и сохраните форму.' });
    onDirty?.();
  };
  const choose = suggestion => {
    if (disabled || suggestion.inn !== inn || current.current.scope !== scope) return;
    const patch = companyPatch(suggestion);
    if (companyConflicts(current.current.form, patch).length) setPending({ scope, suggestion });
    else apply(suggestion);
  };
  const suggestions = Array.isArray(visible?.response?.suggestions) ? visible.response.suggestions : [];
  const message = visible?.error || visible?.response?.message;
  return <Stack spacing={1} sx={{ minWidth: 0 }}>
    {!eligible ? <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
      {(kind === 'LEGAL' ? inn.length === 10 : inn.length === 12) && !validInn(inn)
        ? 'Проверьте ИНН: контрольные цифры не совпадают.'
        : `Введите полный ИНН ${kind === 'LEGAL' ? 'организации — 10 цифр' : 'ИП — 12 цифр'}, чтобы найти реквизиты.`}
    </Typography> : visible?.loading ? <Stack direction="row" alignItems="center" spacing={1}>
      <CircularProgress size="sm" /><Typography level="body-xs">Ищем реквизиты…</Typography>
    </Stack> : visible?.response?.configured === false ? <Typography level="body-xs" color="warning">
      Подсказки реквизитов временно недоступны. Поля можно заполнить вручную.
    </Typography> : message ? <Stack direction="row" alignItems="center" spacing={1}>
      <Typography level="body-xs" color="warning">{message}</Typography>
      <Button size="sm" variant="plain" onClick={() => setRetry(value => value + 1)}>Повторить</Button>
    </Stack> : visible && !suggestions.length ? <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
      Организация не найдена. Проверьте ИНН или заполните реквизиты вручную.
    </Typography> : null}
    {suggestions.map(item => <Sheet key={item.id || `${item.inn}:${item.kpp || ''}:${item.ogrn || ''}`} variant="outlined"
      sx={{ p: 1.25, borderRadius: '12px', minWidth: 0 }}>
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <BusinessRoundedIcon sx={{ color: 'primary.500', fontSize: 20, mt: 0.25 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography level="title-sm" sx={{ wordBreak: 'break-word' }}>{item.company_name || item.value}</Typography>
          <Typography level="body-xs" sx={{ color: 'text.tertiary', wordBreak: 'break-word' }}>
            ИНН {item.inn}{item.kpp ? ` · КПП ${item.kpp}` : ''}{item.legal_address ? ` · ${item.legal_address}` : ''}
          </Typography>
          {item.status && item.status !== 'ACTIVE' && <Typography level="body-xs" color="warning">
            Статус организации: {({ LIQUIDATED: 'ликвидирована', LIQUIDATING: 'ликвидируется', BANKRUPT: 'банкротство', REORGANIZING: 'реорганизация' })[item.status] || item.status}
          </Typography>}
          {Array.isArray(item.omitted_fields) && item.omitted_fields.length > 0 && <Typography level="body-xs" color="warning">
            Часть реквизитов требует ручного заполнения.
          </Typography>}
          <Button size="sm" variant="soft" disabled={disabled} onClick={() => choose(item)} sx={{ mt: 0.75 }}>
            Заполнить реквизиты
          </Button>
        </Box>
      </Stack>
    </Sheet>)}
    {pending?.scope === scope && <Sheet variant="soft" color="warning" sx={{ p: 1.25, borderRadius: '12px' }}>
      <Typography level="body-sm">Некоторые реквизиты уже заполнены. Заменить их данными найденной организации?</Typography>
      <Stack direction="row" sx={{ gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
        <Button size="sm" color="warning" disabled={disabled} onClick={() => apply(pending.suggestion, true)}>Заменить реквизиты</Button>
        <Button size="sm" variant="soft" color="neutral" disabled={disabled} onClick={() => apply(pending.suggestion)}>Только пустые поля</Button>
        <Button size="sm" variant="plain" color="neutral" onClick={() => setPending(null)}>Отмена</Button>
      </Stack>
    </Sheet>}
    {notice?.scope === scope && <Typography level="body-xs" color="success">{notice.value}</Typography>}
    {suggestions.length > 0 && <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
      Данные реестра могут требовать уточнения. Банк и расчётный счёт заполняются вручную.
    </Typography>}
  </Stack>;
}
